import { NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  HostBinding,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
  computed,
  forwardRef,
  inject,
  input,
  output
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import {
  ButtonContentSlotDirective,
  ButtonLabelSlotDirective,
  CheckIndicatorComponent,
  IconComponent,
  RoundedButtonComponent,
  TCssUnitExpression,
  TResponsiveSize
} from '@partners/ui';
import { isEmpty, isNotEmpty, takeOnce } from '@partners/utils';

import { ThemeAssetComponent } from '@partners/themes';
import { filter, map, tap, timer } from 'rxjs';
import { ButtonStateDirective } from '../buttons';
import { BaseCustomControl } from '../custom-control';
import { ButtonToggleGapDirective } from './button-toggle-gap/button-toggle-gap.directive';
import { ButtonToggleIconDirective } from './button-toggle-icon/button-toggle-icon.directive';
import { ButtonToggleItemDirective } from './button-toggle-item/button-toggle-item.directive';
import { ButtonToggleLabelDirective } from './button-toggle-label/button-toggle-label.directive';

@Component({
  selector: 'button-toggle',
  standalone: true,
  imports: [
    NgStyle,
    ReactiveFormsModule,
    NgTemplateOutlet,
    MatButtonToggleModule,
    CheckIndicatorComponent,
    ButtonContentSlotDirective,
    ButtonLabelSlotDirective,
    ButtonToggleItemDirective,
    ButtonToggleGapDirective,
    ButtonStateDirective,
    RoundedButtonComponent,
    ThemeAssetComponent,
    IconComponent
  ],
  template: `
    <mat-button-toggle-group
      [ngStyle]="{ 'align-items': alignItems(), 'justify-content': justifyGroup(), gap: buttonsGap() }"
    >
      @for (option of options$$(); track option; let first = $first, last = $last) {

        @if (option) {

      <div class="button-toggle--item" [ngStyle]="{ flex: childSize() }">
        <rounded-button #currentButton="btnState"
        
            [id]="option[keyProp()]"            
            [size]="buttonSize()"
            [value]="option[valueProp()]"
            [context]="option"
            [buttonState]="option"
            (click)="onButtonClick(option[valueProp()])">

            <ng-template buttonContent>
              @if (iconTemplate?.tpl) {
              <span class="button-toggle--icon" >
                <ng-container *ngTemplateOutlet="iconTemplate?.tpl; context: { $implicit: option }"></ng-container>
              </span>
              }
              @else if(option?.['icon']){
              <span class="button-toggle--icon">
                <icon [loadViaHttp]="true" [iconName]="option['icon']" iconVariant="rounded" />
              </span>
              }
              @if (buttonTemplate?.tpl) {
              <span class="button-toggle--content" >
                <ng-container *ngTemplateOutlet="buttonTemplate?.tpl; context: { $implicit: option }"></ng-container>
              </span>
              }
              @if(currentButton.selected){
                <check-indicator [size]="buttonSize()" />
              }    

            </ng-template>

          </rounded-button>

        @if (labelTemplate?.tpl) {
        <span class="button-toggle--label" (click)="onLabelClick(option[valueProp()])">
          <ng-container *ngTemplateOutlet="labelTemplate.tpl; context: { $implicit: option }"></ng-container>
        </span>
        }
      </div>

      @if (!last && gapTemplate?.tpl) {
      <span class="button-toggle--gap {{ gap() }}">
        <ng-container *ngTemplateOutlet="gapTemplate.tpl; context: { $implicit: option }"></ng-container>
      </span>
      } }}
    </mat-button-toggle-group>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ButtonToggleComponent),
      multi: true
    }
  ],
  host: {
    class: 'button-toggle',
    '[class.button-toggle--wrap]': 'allowWrap()'
  }
})
export class ButtonToggleComponent<T> extends BaseCustomControl<T> implements AfterViewInit {
  /** buttons scale size */
  isGraphicButton = input<boolean>(false);

  /** buttons scale size */
  buttonSize = input<TResponsiveSize>('md');

  /** gap scale size */
  gap = input<TResponsiveSize>();

  allowToggling = input<boolean>(false);
  allowWrap = input<boolean>(false);

  /** limit items per row. otherwise - auto fit the children into a wrapping flex row */
  itemsPerRow = input<number | undefined>(undefined);

  /** vertically align items together inside their container */
  alignItems = input<'center' | 'start' | 'stretch'>('center');

  /** horizontally align items together inside their container */
  justifyGroup = input<'center' | 'start' | 'space-between'>('center');

  /** when true text inside button should break after each word (word per row)  */
  isWordBreakMode = input<boolean>(false);

  buttonsGap = computed<TCssUnitExpression>(() => {
    const gap = this.gap();
    let value = 1;
    switch (gap) {
      case 'lg': {
        value *= 4;
        break;
      }
      case 'md': {
        value *= 2;
        break;
      }
      case 'sm': {
        value *= 1;
        break;
      }
      case 'xs': {
        value *= 0.35;
        break;
      }
      case 'auto': {
        value *= 0;
        break;
      }
      default: {
        value *= 2;
        break;
      }
    }
    return `${value}rem`;
  });

  childSize = computed(() => {
    if (this.itemsPerRow()) {
      return `0 0 calc(${100 / this.itemsPerRow()}% - ${this.buttonsGap()})`;
    }
    return `0 0 auto`;
  });

  /**
   * outputs
   */
  change = output<T>();

  /**
   * template accessors
   */
  @ContentChild(ButtonToggleLabelDirective, { static: true })
  labelTemplate: ButtonToggleLabelDirective;

  @ContentChild(ButtonToggleGapDirective, { static: true })
  gapTemplate: ButtonToggleGapDirective;

  @ContentChild(ButtonToggleIconDirective, { static: true })
  iconTemplate: ButtonToggleIconDirective;

  @ContentChild(ButtonToggleItemDirective, { static: true })
  buttonTemplate: ButtonToggleItemDirective;

  @ViewChildren(ButtonStateDirective)
  buttons: QueryList<ButtonStateDirective>;

  /**
   * host bindings
   */
  @HostBinding('class.button-toggle')
  hostClass = true;

  /**
   * private members
   */
  #changeDetector = inject(ChangeDetectorRef);

  constructor() {
    super();
  }

  ngAfterViewInit(): void {
    // this.#initializeView();
    this.#selectDefaultValue();
  }

  /**
   * initializes the control's once on load. if control is loaded with a valid value -
   * simulate a button click to select the button with the matching value
   */
  #initializeView() {
    // this.onButtonClick(this.control().value as T);
    // /** on load, initiate the control as pristine, so that required validation remain hidden by default */
    // this.control().markAsPristine();
    // this.control().markAsUntouched();
    // this.control().updateValueAndValidity({ emitEvent: true, onlySelf: true });
    // this.#changeDetector.detectChanges();
    // this.#selectDefaultValue();
  }

  /**
   * an initialization (will only run once, on load) function responsible to selecting an option on load.
   * when this control is loaded with a value (not empty) this method will mark the button with that value
   * as selected
   */
  #selectDefaultValue() {
    /**
     * for some strange reason, the "this.buttons" collection remains empty (length: 0) EVEN ON AfterViewInit
     * (this behavior contradicts angular's documentation). to remedy that bug we use timer function to cause
     * a negligible amount of delay in reading the buttons collection (for now - DO NOT REMOVE timer(0))
     */
    timer(0)
      .pipe(
        takeOnce(),
        map(() => {
          // Console.logInfo(`found ${this.buttons?.length} buttons in "${this.name()}" toggle group`);
          return this.buttons.find((b) => b.value == this.value());
        }),
        filter((selectedButton: ButtonStateDirective) => isNotEmpty(selectedButton)),
        tap((selectedButton) => this.onButtonClick(selectedButton.value as T))
      )
      .subscribe();
  }

  override writeValue(value: T | undefined): void {
    super.writeValue(value);

    if (!this.#hasRenderedButtons()) {
      return;
    }

    isEmpty(value) ? this.#resetButtons() : this.#selectButtons(value);
  }

  onLabelClick(value: T = this.value()) {
    this.onButtonClick(value);
  }

  onButtonClick(value: T = this.value()) {
    this.writeValue(value);
    this.onTouched();
    this.onChange(value);
    this.#selectButtons(value);

    this.change.emit(value);

    this.#changeDetector.detectChanges();
  }

  #hasRenderedButtons() {
    return this.buttons?.length > 0;
  }

  /**
   * @description
   * control the buttons as a group, meaning, once a single button's state changes -
   * the button's siblings' state are affected two, example: if a button is selected
   * and the group is in a single selection mode, we need to unselect the previous
   * selection
   *
   * the clicked button changes its own state (selected, touched etc')
   *
   * we need to iterate its siblings and manually change their state
   *
   * @param option the value entry attached to the button (likely to be {key... value...})
   */
  #selectButtons(value: T) {
    this.buttons.forEach((button) => {
      /** for single selection mode we need to unselect every sibling of the selected button */
      const isSelectedButton = button.data[this.valueProp()] == value;
      button.updateState({
        selected: isSelectedButton,
        touched: button.touched || isSelectedButton,
        dirty: button.dirty || (!isSelectedButton && isNotEmpty(value))
      });
    });

    this.#changeDetector.detectChanges();
  }

  /**
   * reset all buttons in the collection to a non-selected + pristine state
   */
  #resetButtons() {
    this.buttons.forEach((button) => {
      button.updateState({
        selected: false,
        touched: false,
        dirty: false
      });
    });

    this.#changeDetector.detectChanges();
  }
}
